import React, { useState, useMemo } from 'react';
import {
    format,
    startOfMonth,
    endOfMonth,
    eachDayOfInterval,
    isSameDay,
    isSameMonth,
    addMonths,
    subMonths,
    getDay,
    startOfWeek,
    addDays
} from 'date-fns';
import { enUS, it } from 'date-fns/locale';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useEvents } from '@/contexts/EventsContext';
import { DAYS_ORDER } from '@/lib/constants';
import { useTranslation } from 'react-i18next';

interface OccupancyCalendarProps {
    currentDate: Date;
    onSelectDate: (date: Date) => void;
    onClose: () => void;
}

const OccupancyCalendar: React.FC<OccupancyCalendarProps> = ({ currentDate, onSelectDate, onClose }) => {
    const { t, i18n } = useTranslation();
    const { organization } = useAuth();
    const { bookings, busySlots, getPerformanceById } = useEvents();
    const [viewDate, setViewDate] = useState(currentDate);

    // Determine date-fns locale
    const dateLocale = i18n.language === 'it' ? it : enUS;

    // Helper to parse time string "HH:mm" to minutes from midnight
    const getMinutesFromMidnight = (timeStr: string) => {
        const [h, m] = timeStr.split(':').map(Number);
        return h * 60 + m;
    };

    const calendarDays = useMemo(() => {
        const start = startOfMonth(viewDate);
        const end = endOfMonth(viewDate);

        // Adjust start to beginning of week (Monday)
        const startWeek = getDay(start); // 0 = Sun, 1 = Mon...
        // We want Monday (1) to be first. 
        // If start is Sunday (0), we backtrack 6 days.
        // If start is Monday (1), backtrack 0.
        // specific logic for Monday start:
        const daysToBacktrack = startWeek === 0 ? 6 : startWeek - 1;

        const firstDayOfGrid = new Date(start);
        firstDayOfGrid.setDate(start.getDate() - daysToBacktrack);

        const lastDayOfGrid = new Date(end);
        // Fill the rest of the week
        const endWeek = getDay(end);
        const daysForward = endWeek === 0 ? 0 : 7 - endWeek;
        lastDayOfGrid.setDate(end.getDate() + daysForward);

        return eachDayOfInterval({ start: firstDayOfGrid, end: lastDayOfGrid });
    }, [viewDate]);

    // Generate week day headers dynamically based on locale (Mon start)
    const weekDays = useMemo(() => {
        const start = startOfWeek(new Date(), { weekStartsOn: 1 });
        return Array.from({ length: 7 }).map((_, i) =>
            format(addDays(start, i), 'EEE', { locale: dateLocale })
        );
    }, [dateLocale]);

    // Calculate Occupancy for a Day
    const getDayStatus = (date: Date) => {
        const dayKey = DAYS_ORDER[getDay(date) === 0 ? 6 : getDay(date) - 1]; // Convert Date day (0-6 Sun-Sat) to DAYS_ORDER key
        const timeRange = organization?.time_ranges?.[dayKey];

        // 1. Check if Closed (by schedule)
        if (!timeRange || !timeRange.open) return { type: 'closed', percent: 0 };

        // 2. Check if Holiday (by busySlots)
        // Check local date string match
        const dateStr = format(date, 'yyyy-MM-dd');
        const isHoliday = busySlots.some((s: any) =>
            s.type === 'holiday' &&
            !s.hairdresser_id && // Shop wide holiday
            s.start_date <= dateStr &&
            s.end_date >= dateStr
        );
        if (isHoliday) return { type: 'closed', percent: 0 };

        // 3. Calculate Total Slots (15 mins)
        let totalMinutes = 0;
        const openMin = getMinutesFromMidnight(timeRange.open);
        const closeMin = getMinutesFromMidnight(timeRange.close);

        if (timeRange.start_break && timeRange.end_break) {
            const breakStart = getMinutesFromMidnight(timeRange.start_break);
            const breakEnd = getMinutesFromMidnight(timeRange.end_break);
            totalMinutes = (breakStart - openMin) + (closeMin - breakEnd);
        } else {
            totalMinutes = closeMin - openMin;
        }

        const totalSlots = totalMinutes / 15;
        if (totalSlots <= 0) return { type: 'closed', percent: 0 };

        // 4. Calculate Occupied Slots
        // Filter bookings for this day
        const dayBookings = bookings.filter((b: any) =>
            b.status !== 'cancelled' &&
            b.date === dateStr
        );

        let occupiedMinutes = 0;
        dayBookings.forEach((b: any) => {
            const perf = getPerformanceById(b.performance_id);
            const duration = perf?.duration || 30; // Default 30 min
            occupiedMinutes += duration;
        });

        const occupiedSlots = occupiedMinutes / 15;

        let percent = (occupiedSlots / totalSlots) * 100;

        // Cap logic if needed, but user wants specific colors
        return { type: 'open', percent };
    };

    const handlePrevMonth = () => setViewDate(subMonths(viewDate, 1));
    const handleNextMonth = () => setViewDate(addMonths(viewDate, 1));

    return (
        // Su schermi stretti si restringe invece di uscire dal bordo: il pannello è
        // ancorato a destra e i 56px sono i margini di pagina + header dei due lati.
        // Il max-width è inline apposta: Tailwind qui è il CDN a runtime e sugli
        // arbitrary value con calc() non si può fare affidamento.
        <div
            style={{ maxWidth: 'calc(100vw - 56px)' }}
            className="p-4 bg-white rounded-xl shadow-xl border border-gray-100 w-[320px]"
        >
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                    <ChevronLeft size={20} className="text-gray-500" />
                </button>
                <h3 className="text-md font-bold text-gray-900 capitalize">
                    {format(viewDate, 'MMMM yyyy', { locale: dateLocale })}
                </h3>
                <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
                    <ChevronRight size={20} className="text-gray-500" />
                </button>
            </div>

            {/* Week Days */}
            <div className="grid grid-cols-7 mb-2">
                {weekDays.map((d, i) => (
                    <div key={i} className="text-center text-xs font-bold text-gray-400 py-1 capitalize">
                        {d}
                    </div>
                ))}
            </div>

            {/* Days Grid */}
            <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((day, idx) => {
                    const isCurrentMonth = isSameMonth(day, viewDate);
                    const isToday = isSameDay(day, new Date());
                    const isSelected = isSameDay(day, currentDate);
                    const status = getDayStatus(day);

                    let bgClass = 'bg-white hover:bg-gray-50 text-gray-900';
                    let dotClass = '';

                    if (!isCurrentMonth) {
                        bgClass = 'text-gray-300 pointer-events-none opacity-50'; // Disable other months interactions? or just style
                    } else if (status.type === 'closed') {
                        bgClass = 'bg-gray-50 text-gray-300 pointer-events-none';
                    } else {
                        // Color Logic
                        if (status.percent >= 100) {
                            dotClass = 'bg-red-500';
                        } else if (status.percent > 50) {
                            dotClass = 'bg-orange-400';
                        } else {
                            dotClass = 'bg-green-500';
                        }

                        if (isSelected) {
                            bgClass = 'bg-clip-600 text-white shadow-md hover:bg-clip-700';
                            dotClass = 'bg-white'; // White dot on selected
                        } else if (isToday) {
                            bgClass += ' ring-1 ring-inset ring-gray-200 font-extrabold text-clip-700 bg-clip-50';
                        }
                    }

                    return (
                        <button
                            key={idx}
                            onClick={() => {
                                onSelectDate(day);
                                onClose();
                            }}
                            disabled={!isCurrentMonth || status.type === 'closed'}
                            className={`
                                relative h-9 rounded-lg flex items-center justify-center text-sm font-medium transition-all
                                ${bgClass}
                            `}
                        >
                            {format(day, 'd')}

                            {/* Occupancy Dot */}
                            {status.type === 'open' && isCurrentMonth && (
                                <div className={`absolute bottom-1 w-1 h-1 rounded-full ${dotClass}`} />
                            )}
                        </button>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="mt-4 flex items-center justify-between px-2">
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-[10px] text-gray-500 font-medium">{t('calendar.free')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-orange-400" />
                    <span className="text-[10px] text-gray-500 font-medium">{t('calendar.busy')}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-red-500" />
                    <span className="text-[10px] text-gray-500 font-medium">{t('calendar.full')}</span>
                </div>
            </div>
        </div>
    );
};

export default OccupancyCalendar;
